import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { useCandidatoContext } from "../../context/CandidatoContext";
import { useAuth } from "../../context/AuthContext";
import {
  listarNotificacoes,
  contarNotificacoesNaoLidas,
  marcarNotificacaoLida,
  marcarTodasNotificacoesLidas,
} from "../../api/notificacoes";

interface Notificacao {
  id: number;
  tipo: string;
  titulo: string;
  descricao: string | null;
  link: string | null;
  lida: boolean;
  criado_em: string;
}

const ICONE_TIPO: Record<string, string> = {
  tarefa: "TA",
  mensagem: "MS",
  solicitacao: "SO",
  lideranca: "LD",
  meta: "MT",
};

const COR_TIPO: Record<string, string> = {
  tarefa: "bg-warning-500",
  mensagem: "bg-blue-light-500",
  solicitacao: "bg-brand-500",
  lideranca: "bg-success-500",
  meta: "bg-error-500",
};

function tempoRelativo(dataIso: string) {
  const diffMs = Date.now() - new Date(dataIso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min atrás`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `${horas} h atrás`;
  const dias = Math.floor(horas / 24);
  return `${dias} d atrás`;
}

// Puxa a contagem de não lidas periodicamente pro sino piscar mesmo sem abrir
// o dropdown — mensagem/tarefa/solicitação podem chegar de outra aba.
const INTERVALO_CONTAGEM_MS = 30000;

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selecionado } = useCandidatoContext();
  const candidatoId = selecionado || user?.candidato_id || null;

  const [isOpen, setIsOpen] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const [lista, setLista] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(false);

  const atualizarContagem = useCallback(() => {
    contarNotificacoesNaoLidas(candidatoId)
      .then((r: { total: number }) => setNaoLidas(r.total))
      .catch(() => {});
  }, [candidatoId]);

  const carregarLista = useCallback(() => {
    setCarregando(true);
    listarNotificacoes(false, candidatoId)
      .then(setLista)
      .finally(() => setCarregando(false));
  }, [candidatoId]);

  useEffect(() => {
    atualizarContagem();
    if (isOpen) {
      carregarLista();
    }
    const t = setInterval(atualizarContagem, INTERVALO_CONTAGEM_MS);
    return () => clearInterval(t);
  }, [atualizarContagem, carregarLista, isOpen]);

  function toggleDropdown() {
    const abrindo = !isOpen;
    setIsOpen(abrindo);
    if (abrindo) {
      carregarLista();
    }
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  const abrirNotificacao = async (n: Notificacao) => {
    closeDropdown();
    if (!n.lida) {
      await marcarNotificacaoLida(n.id);
      setNaoLidas((v) => Math.max(0, v - 1));
    }
    if (n.link) navigate(n.link);
  };

  const marcarTodas = async () => {
    await marcarTodasNotificacoesLidas(candidatoId);
    setLista((l) => l.map((n) => ({ ...n, lida: true })));
    setNaoLidas(0);
  };


  return (
    <div className="relative">
      <button
        className="relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        onClick={toggleDropdown}
      >
        <span
          className={`absolute right-0 top-0.5 z-10 h-2 w-2 rounded-full bg-orange-400 ${
            naoLidas === 0 ? "hidden" : "flex"
          }`}
        >
          <span className="absolute inline-flex w-full h-full bg-orange-400 rounded-full opacity-75 animate-ping"></span>
        </span>
        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute -right-[240px] mt-[17px] flex h-[480px] w-[350px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px] lg:right-0"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Notificações</h5>
          {naoLidas > 0 && (
            <button
              onClick={marcarTodas}
              className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Marcar todas como lidas
            </button>
          )}
        </div>
        <ul className="flex flex-col h-auto overflow-y-auto custom-scrollbar">
          {carregando && <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">Carregando...</p>}
          {!carregando && lista.length === 0 && (
            <p className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">Nenhuma notificação por aqui.</p>
          )}
          {!carregando &&
            lista.map((n) => (
              <li key={n.id}>
                <DropdownItem
                  onItemClick={() => abrirNotificacao(n)}
                  className={`flex gap-3 rounded-lg border-b border-gray-100 p-3 px-4.5 py-3 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-white/5 ${
                    n.lida ? "" : "bg-brand-50/50 dark:bg-brand-500/5"
                  }`}
                >
                  <span
                    className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
                      COR_TIPO[n.tipo] || "bg-gray-400"
                    }`}
                  >
                    {ICONE_TIPO[n.tipo] || "NT"}
                    {!n.lida && (
                      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-[1.5px] border-white bg-orange-400 dark:border-gray-900" />
                    )}
                  </span>

                  <span className="block min-w-0">
                    <span className="mb-1 block truncate text-theme-sm font-medium text-gray-800 dark:text-white/90">
                      {n.titulo}
                    </span>
                    {n.descricao && (
                      <span className="mb-1 block truncate text-theme-xs text-gray-500 dark:text-gray-400">
                        {n.descricao}
                      </span>
                    )}
                    <span className="text-gray-500 text-theme-xs dark:text-gray-400">
                      {tempoRelativo(n.criado_em)}
                    </span>
                  </span>
                </DropdownItem>
              </li>
            ))}
        </ul>
      </Dropdown>
    </div>
  );
}
